// File path: /core/frontend/src/context/AuthContext.tsx
// Authentication Context - Shared across all modules

import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { toast } from 'react-toastify';
import authService from '../services/authService';
import userService from '../services/userService';
// Import User type from the types directory
import type { User, AuthContextType as AuthContextInterface } from '../types/user';

// Define the Cognito auth challenge response type
interface CognitoAuthResponse {
  challengeName?: string;
  [key: string]: any;
}

// Extend the AuthContextType to include additional methods
interface AuthContextType extends Omit<AuthContextInterface, 'login'> {
  login: (username: string, password: string) => Promise<CognitoAuthResponse | void>;
  forgotPassword: (username: string) => Promise<void>;
  resetPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  // `any` on purpose: Elevate's unrouted copy of the first-login page still passes
  // its old fields. authService sends only given_name, family_name and
  // preferred_username, whatever arrives here (P-HARD H5).
  completeNewPassword: (user: any, newPassword: string, requiredAttributes: any) => Promise<any>;
  refreshToken: () => Promise<string | null>;
  updateProfile: (attributes: Partial<User>) => Promise<void>;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  login: async () => {},
  logout: async () => {},
  forgotPassword: async () => {},
  resetPassword: async () => {},
  completeNewPassword: async () => {},
  refreshToken: async () => null,
  updateProfile: async () => {},
});

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component to wrap the app with
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);

  // Use refs to prevent duplicate calls and store intervals
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingAuthRef = useRef<boolean>(false);
  const hubListenerRef = useRef<any>(null);
  const hasInitializedRef = useRef<boolean>(false); // NEW: Prevent duplicate initialization

  // DEBUG: Add state change logging
  useEffect(() => {
    console.log('🔄 AUTH STATE CHANGE:', {
      isAuthenticated,
      isLoading,
      user: user ? {
        id: user.id,
        email: user.email,
        role: user.role,
        schoolId: user.schoolId,
        districtId: user.districtId,
        tenantId: user.tenant_id
      } : null
    });
  }, [isAuthenticated, isLoading, user]);

  // Check authentication status on mount - FIXED to prevent duplicates
  useEffect(() => {
    // Prevent duplicate initialization
    if (hasInitializedRef.current) {
      console.log('🚫 Auth already initialized, skipping...');
      return;
    }

    console.log('🚀 Initializing authentication...');
    hasInitializedRef.current = true;

    checkAuthState();

    // Set up Hub listener for auth events
    const hubListener = Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          console.log('🎉 User signed in');
          // Don't call checkAuthState here as it's already handled by login
          break;
        case 'signOut':
          console.log('👋 User signed out');
          clearAuthState();
          break;
        case 'tokenRefresh':
          console.log('🔄 Token refreshed');
          break;
        case 'tokenRefresh_failure':
          console.error('❌ Token refresh failed');
          clearAuthState();
          toast.error('Session expired. Please login again.');
          break;
      }
    });

    // Store hub listener reference
    hubListenerRef.current = hubListener;

    // Cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up auth provider...');
      if (hubListenerRef.current) {
        Hub.remove('auth', hubListenerRef.current);
      }
      stopTokenCheck();
      hasInitializedRef.current = false; // Reset for next mount
    };
  }, []); // Empty dependency array ensures this only runs once

  // Start periodic token check
  const startTokenCheck = () => {
    // Clear any existing interval
    stopTokenCheck();

    // Check token every 5 minutes
    tokenCheckIntervalRef.current = setInterval(async () => {
      try {
        console.log('⏰ Performing periodic token check...');

        const session = await Auth.currentSession();
        const token = session.getAccessToken();
        const exp = token.getExpiration();
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = exp - now;

        console.log(`⏳ Token expires in ${Math.floor(timeUntilExpiry / 60)} minutes`);

        // If token expires in less than 10 minutes, proactively refresh
        if (timeUntilExpiry < 600) {
          console.log('🔄 Proactively refreshing token...');
          await Auth.currentSession(); // This will auto-refresh if needed
          console.log('✅ Token refreshed proactively');
        }
      } catch (error) {
        console.error('❌ Token check failed:', error);
        // If token check fails, user might be logged out
        checkAuthState();
      }
    }, 5 * 60 * 1000); // 5 minutes
  };

  // Stop token check
  const stopTokenCheck = () => {
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
      tokenCheckIntervalRef.current = null;
    }
  };

  // Enhanced function to fetch user's organizational data from database
  const fetchUserOrganizationalData = async (cognitoUsername: string, userEmail: string) => {
    try {
      console.log('🔍 Fetching organizational data for user:', cognitoUsername);

      let dbUser = null;

      try {
        // First try by cognito_username
        dbUser = await userService.getUserByCognitoUsername(cognitoUsername);
        console.log('✅ Found user by Cognito username:', dbUser);
      } catch (error) {
        console.log('⚠️ User not found by cognito_username, trying by email...');
        try {
          // Fallback to email lookup
          dbUser = await userService.getUserByEmail(userEmail);
          console.log('✅ Found user by email:', dbUser);
        } catch (emailError) {
          console.error('❌ User not found by email either:', emailError);
          return null;
        }
      }

      if (dbUser) {
        console.log('✅ Found user organizational data:', {
          schoolId: dbUser.schoolId,
          districtId: dbUser.districtId,
          programId: dbUser.programId,
          stateId: dbUser.stateId,
          tenantId: dbUser.tenant_id || dbUser.tenantId
        });

        return {
          schoolId: dbUser.schoolId,
          districtId: dbUser.districtId,
          programId: dbUser.programId,
          stateId: dbUser.stateId,
          tenant_id: dbUser.tenant_id || dbUser.tenantId,
          organization_id: dbUser.schoolId || dbUser.districtId,
          subjects: dbUser.subjects,
          grades: dbUser.grades,
          program_affiliation: dbUser.program_affiliation
        };
      }

      return null;
    } catch (error) {
      console.error('❌ Error fetching user organizational data:', error);
      // For development, return mock data if API fails
      console.log('🧪 Using mock organizational data for development');
      return {
        schoolId: '3dd5039f-6f0d-4f90-b04b-04b4bc52c092',
        districtId: 'demo-district-1',
        programId: 'project-momentum-az',
        stateId: 'AZ',
        tenant_id: 'AZ:demo-district-1:3dd5039f-6f0d-4f90-b04b-04b4bc52c092',
        organization_id: '3dd5039f-6f0d-4f90-b04b-04b4bc52c092',
        subjects: ['Mathematics', 'Reading'],
        grades: ['K', '1', '2', '3', '4', '5'],
        program_affiliation: 'Project Momentum Arizona'
      };
    }
  };

  const checkAuthState = async () => {
    // Prevent multiple simultaneous auth checks
    if (isCheckingAuthRef.current) {
      console.log('🔄 Auth check already in progress, skipping...');
      return;
    }

    console.log('🚀 Starting auth state check...');

    try {
      isCheckingAuthRef.current = true;
      setIsLoading(true);
      console.log('⏳ Set loading to true');

      // Get the current authenticated user
      const cognitoUser = await Auth.currentAuthenticatedUser();

      // Get the current session to access tokens
      const session = await Auth.currentSession();
      console.log('✅ Got Cognito user and session');

      // Check if token is valid
      const token = session.getAccessToken();
      const exp = token.getExpiration();
      const now = Math.floor(Date.now() / 1000);

      if (exp < now) {
        console.log('⚠️ Token is expired, attempting refresh...');
        // Token is expired, Auth.currentSession() should auto-refresh
        await Auth.currentSession();
      }

      // Get access token
      const accessToken = session.getAccessToken().getJwtToken();

      // Store the access token in localStorage for API calls
      localStorage.setItem('accessToken', accessToken);
      console.log('💾 Access token stored');

      // Get user attributes from the Cognito user object
      const userAttributes = cognitoUser.attributes;

      // Get groups from ID token
      const idToken = session.getIdToken();
      let groups = [];

      // Extract groups from ID token payload
      if (idToken.payload['cognito:groups']) {
        groups = idToken.payload['cognito:groups'];
        console.log('✅ Found groups in ID token payload:', groups);
      }

      console.log('🔍 Raw Cognito user:', cognitoUser);
      console.log('🔍 User attributes:', userAttributes);
      console.log('🔍 Final extracted groups:', groups);

      // Fetch organizational data from database
      console.log('🗄️ Fetching organizational data from database...');
      const orgData = await fetchUserOrganizationalData(cognitoUser.username, userAttributes.email);
      console.log('📋 Org data received:', orgData);

      // Transform the user data to match our User type
      const transformedUser: User = {
        id: cognitoUser.username || '',
        username: cognitoUser.username || '',
        email: userAttributes.email || '',
        given_name: userAttributes.given_name || '',
        family_name: userAttributes.family_name || '',
        // UI-only role source: RoleBasedRedirect and the route guards read it until
        // a later task moves the UI to the Cognito groups. The server never trusts
        // it (the authorizer uses groups + core.users), and once the app clients'
        // WriteAttributes exclude custom:* (P-HARD H5, A2c) users cannot change it.
        role: (userAttributes['custom:role'] || 'Teacher') as User['role'],

        // Use database data first, then Cognito attributes as fallback
        tenant_id: orgData?.tenant_id || userAttributes['custom:tenant_id'] || '',
        schoolId: orgData?.schoolId || userAttributes['custom:schoolId'] || userAttributes['custom:school_id'],
        districtId: orgData?.districtId || userAttributes['custom:districtId'] || userAttributes['custom:district_id'],
        programId: orgData?.programId || userAttributes['custom:programId'] || userAttributes['custom:program_id'],
        stateId: orgData?.stateId || userAttributes['custom:stateId'] || userAttributes['custom:state_id'],

        // Legacy support
        organization_id: orgData?.organization_id || userAttributes['custom:organization_id'],

        groups: groups,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),

        // Optional fields from database
        subjects: orgData?.subjects,
        grades: orgData?.grades,
        program_affiliation: orgData?.program_affiliation || userAttributes['custom:program_affiliation'],
      };

      console.log('🏗️ Built transformed user:', transformedUser);

      // CRITICAL: Use functional updates to ensure state consistency
      console.log('📝 Setting user state with functional update...');
      setUser(() => {
        console.log('🔄 User state setter called with:', transformedUser);
        return transformedUser;
      });

      console.log('✅ Setting authenticated to true...');
      setIsAuthenticated(() => {
        console.log('🔄 Auth state setter called: true');
        return true;
      });

      // Store user info in localStorage for quick access
      localStorage.setItem('userRole', transformedUser.role);
      localStorage.setItem('userGroups', transformedUser.groups.join(','));
      localStorage.setItem('userTenantId', transformedUser.tenant_id);
      if (transformedUser.schoolId) localStorage.setItem('userSchoolId', transformedUser.schoolId);
      if (transformedUser.districtId) localStorage.setItem('userDistrictId', transformedUser.districtId);
      if (transformedUser.programId) localStorage.setItem('userProgramId', transformedUser.programId);
      if (transformedUser.stateId) localStorage.setItem('userStateId', transformedUser.stateId);

      console.log('💾 Stored user data in localStorage');
      console.log('User authenticated with organizational data:', transformedUser);
      console.log('Access token stored for API calls');

      // Validation warnings but don't block login
      if (transformedUser.role === 'SchoolAdmin' && !transformedUser.schoolId) {
        console.warn('⚠️ SchoolAdmin user missing schoolId assignment');
        toast.warn('Your account is missing school assignment. Please contact your administrator.');
      }

      // Start token check interval
      startTokenCheck();
      console.log('✅ Auth state check completed successfully');

    } catch (error) {
      console.log('❌ Auth check failed:', error);
      clearAuthState();
    } finally {
      console.log('🏁 Setting loading to false');
      setIsLoading(() => {
        console.log('🔄 Loading state setter called: false');
        return false;
      });
      isCheckingAuthRef.current = false;
    }
  };

  // Clear auth state
  const clearAuthState = () => {
    console.log('🧹 Clearing auth state...');
    setUser(null);
    setIsAuthenticated(false);
    clearLocalStorage();
    stopTokenCheck();
    console.log('✅ Auth state cleared');
  };

  // Update the clearLocalStorage function to include accessToken
  const clearLocalStorage = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userGroups');
    localStorage.removeItem('userTenantId');
    localStorage.removeItem('userSchoolId');
    localStorage.removeItem('userDistrictId');
    localStorage.removeItem('userProgramId');
    localStorage.removeItem('userStateId');
  };

  // Login function
  const login = async (username: string, password: string): Promise<CognitoAuthResponse | void> => {
    try {
      setIsLoading(true);
      const response = await Auth.signIn(username, password);

      // Handle NEW_PASSWORD_REQUIRED challenge
      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        return response; // Return the response for the component to handle the challenge
      }

      // If login successful, get the full user data
      await checkAuthState();
      toast.success('Login successful!');
      return response;
    } catch (error: any) {
      toast.error(error.message || 'Failed to login');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Complete new password challenge
  const completeNewPassword = async (user: any, newPassword: string, requiredAttributes: any) => {
    try {
      setIsLoading(true);
      const result = await authService.completeNewPassword(user, newPassword, requiredAttributes);

      // After completing new password, get full user data including org info
      await checkAuthState();

      toast.success('Password set successfully!');
      return result;
    } catch (error: any) {
      toast.error(error.message || 'Failed to set new password');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setIsLoading(true);
      await authService.logout();
      clearAuthState();
      toast.info('You have been logged out');
    } catch (error: any) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      clearAuthState();
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password function
  const forgotPassword = async (username: string) => {
    try {
      await authService.forgotPassword(username);
      toast.info('Password reset code sent to your email');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send password reset code');
      throw error;
    }
  };

  // Reset password function
  const resetPassword = async (username: string, code: string, newPassword: string) => {
    try {
      await authService.resetPassword(username, code, newPassword);
      toast.success('Password reset successful. Please login with your new password.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset password');
      throw error;
    }
  };

  // Refresh authentication token
  const refreshToken = async (): Promise<string | null> => {
    try {
      console.log('🔄 Manually refreshing token...');
      const session = await Auth.currentSession();
      const token = session.getAccessToken().getJwtToken();

      // Update stored token
      localStorage.setItem('accessToken', token);

      console.log('✅ Token refreshed manually');
      return token;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  };

  // Update user profile
  const updateProfile = async (attributes: Partial<User>) => {
    try {
      setIsLoading(true);

      // Names only: a user never writes their own role, tenant or any custom:*
      // attribute (P-HARD H5); those come from groups + core.users, set by an admin.
      const cognitoAttributes: { given_name?: string; family_name?: string } = {};
      if (attributes.given_name) cognitoAttributes.given_name = attributes.given_name;
      if (attributes.family_name) cognitoAttributes.family_name = attributes.family_name;

      await authService.updateUserAttributes(cognitoAttributes);

      // If updating organizational data, also update the database
      if (attributes.schoolId || attributes.districtId || attributes.programId || attributes.stateId) {
        console.log('🗄️ Updating organizational data in database...');
        // You would call your user service to update the database record here
        // await userService.updateUser(user.id, attributes);
      }

      // Refresh user data
      await checkAuthState();
      toast.success('Profile updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update profile');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Provide the auth context to the app
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        login,
        logout,
        forgotPassword,
        resetPassword,
        completeNewPassword,
        refreshToken,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};